import { Modals } from '../contexts/ModalsContext';
import { CoverageProps, ProductItemOriginal, ProductItemProps } from '../models/Product';
import { Resource, ScheduleItemProps, typeLocationSchedule } from '../models/Schedule';
import { AdditionalInformations } from '../modules/validateRules';
import { PAGES } from '../flows/General/router/pages';
import { INFO_GENERIC_RULES_CONDITION } from '../utils/constants';
import { findGenericModalByCondition } from '../utils/genericModals';
import { PatientProps, usePatientContext } from '../contexts/PatientContext';

export type ProductRuleResponse = {
  type: string;
  coverage?: CoverageProps;
};

export const TYPE_ROUTE_PRODUCT = {
  ok: 'ok',
  is_first_appointment: INFO_GENERIC_RULES_CONDITION.is_first_appointment,
  only_telemedicine: 'only_telemedicine'
};

export type useProductRuleActionsProps = {
  goTo: (route: number, state?: any) => void;
  openModal: (modal: 'PRODUCT_NOT_COVERED' | 'SERVICE_UNAVAILABLE' | 'NINE_YEARS_DRIVE_THRU') => void;
  schedule?: ScheduleItemProps;
};

export const useProductRuleActions = ({ goTo, openModal, schedule }: useProductRuleActionsProps) => {
  function handleTelemedicineProduct(product: ProductItemProps | undefined): ProductRuleResponse | null {
    if (product?.coverages?.length === 1) {
      const [coverage] = product.coverages;
      if (coverage.care_type === typeLocationSchedule.TELEMEDICINE) {
        return { type: TYPE_ROUTE_PRODUCT.only_telemedicine, coverage };
      }
    }
    return null;
  }

  function makeResponse(type?: string) {
    const responseRoute: { [index: string]: () => ProductRuleResponse | undefined } = {
      is_first_appointment: () => ({ type: INFO_GENERIC_RULES_CONDITION.is_first_appointment }),
      ok: () => {
        const IS_PREGNANT = !!schedule?.patient?.pregnant;
        const IS_EMPLOYEE = schedule?.payment?.eligibility?.plan.toUpperCase().includes('ALBERT EINSTEIN');

        if (IS_PREGNANT && IS_EMPLOYEE) goTo(PAGES.PREGNANT_INFO.id);
        else goTo(PAGES.SERVICE_TYPE_PAGE.id);

        return { type: TYPE_ROUTE_PRODUCT.ok };
      },
      default: () => {
        openModal(Modals.PRODUCT_NOT_COVERED);
        return undefined;
      }
    };
    return type ? responseRoute[type]() : responseRoute.default();
  }

  function routeScheduleByProduct(
    product: ProductItemOriginal,
    resource: Resource | null,
    genericModals?: AdditionalInformations[]
  ): ProductRuleResponse | undefined {
    if (product.coverages?.length) {
      if (!product.is_b2b) {
        return makeResponse(TYPE_ROUTE_PRODUCT.ok);
      }
      if (product.is_b2b && resource) {
        const productIsOnlyTemeledicine = handleTelemedicineProduct(product);
        if (productIsOnlyTemeledicine) return productIsOnlyTemeledicine;
        if (!productIsOnlyTemeledicine) {
          return makeResponse(TYPE_ROUTE_PRODUCT.ok);
        }
      }
      if (product.is_b2b && !resource) {
        if (genericModals?.length) {
          const genericModal = findGenericModalByCondition({
            condition: INFO_GENERIC_RULES_CONDITION.is_first_appointment,
            genericModals: genericModals
          });
          if (genericModal) return makeResponse(TYPE_ROUTE_PRODUCT.is_first_appointment);
        }
        return makeResponse(TYPE_ROUTE_PRODUCT.ok);
      }
    }
    return makeResponse();
  }
  return { routeScheduleByProduct, handleTelemedicineProduct };
};
